import { ArrowRight, Building2, FileSearch, Scale, Bell } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { setStartDismissed, startDismissed } from '@/lib/landing';

interface Task {
  key: string;
  icon: LucideIcon;
  /** The screen, already filled in, so the first click shows an answer rather than a form. */
  to: string;
}

/**
 * Four first tasks, each opening the screen that answers it with the inputs filled. The page
 * holds no figures of its own: every number a reader sees after a click comes from that
 * screen, with its own population and as-of strip.
 */
export const START_TASKS: readonly Task[] = [
  { key: 'precedents', icon: FileSearch, to: '/cade/documents?q=THC2' },
  { key: 'company', icon: Building2, to: '/cade/company?q=Ambev' },
  { key: 'fines', icon: Scale, to: '/cade/positioning?conduct=cartel' },
  { key: 'sectors', icon: Bell, to: '/workspace' },
];

/**
 * The CADE first-steps page. CADE opens here until the reader dismisses it (a per-browser flag,
 * `lib/landing.ts`); it stays in the menu either way.
 */
export function CadeStartPage() {
  const { t } = useTranslation(['cade']);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(startDismissed);

  const dismiss = () => {
    setStartDismissed(true);
    setDismissed(true);
    navigate('/cade/decisions');
  };
  const restore = () => {
    setStartDismissed(false);
    setDismissed(false);
  };

  return (
    <div className="space-y-4 p-6" data-start>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:start.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:start.subtitle')}</p>
      </header>

      <ol className="grid gap-3 sm:grid-cols-2">
        {START_TASKS.map(({ key, icon: Icon, to }, index) => (
          <li key={key} className="space-y-2 rounded-md border p-4" data-start-task={key}>
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs tabular-nums text-foreground">
                {index + 1}
              </span>
              <Icon className="h-4 w-4 text-primary" aria-hidden />
              <h2 className="text-sm font-semibold text-foreground">
                {t(`cade:start.tasks.${key}.title`)}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">{t(`cade:start.tasks.${key}.body`)}</p>
            <Link
              to={to}
              className="inline-flex items-center gap-1 text-sm text-primary underline underline-offset-4"
            >
              {t(`cade:start.tasks.${key}.action`)}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </li>
        ))}
      </ol>

      <p className="text-sm text-muted-foreground">
        {t('cade:start.method')}{' '}
        <Link to="/cade/method" className="text-primary underline underline-offset-4">
          {t('cade:start.methodLink')}
        </Link>
      </p>

      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        {dismissed ? (
          <>
            <span className="text-xs text-muted-foreground" data-start-dismissed>
              {t('cade:start.dismissedNote')}
            </span>
            <button
              type="button"
              onClick={restore}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground"
            >
              {t('cade:start.restore')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md border px-3 py-1.5 text-sm text-foreground"
            data-start-dismiss
          >
            {t('cade:start.dismiss')}
          </button>
        )}
      </div>
    </div>
  );
}
